/* ==========================================================================
   FROSTPEPPER LABS — CORE BEHAVIOUR
   Vanilla JS. No dependencies. Deferred. ~11KB unminified.
   Every module is defensive: a missing element never throws.
   --------------------------------------------------------------------------
   01 Utilities
   02 Logo intro
   03 Scroll reveal
   04 Header behaviour
   05 Announcement rotator
   06 Mobile nav
   07 On Ice wishlist
   08 Reveal slider
   09 Countdown + unit meter
   10 Count-up stats
   11 Accordions + tabs + modals
   12 Sticky add-to-cart
   13 Quantity + quick order
   14 Unit toggle (cm / in)
   15 Marquee
   16 Cart drawer helpers
   17 Buy box (Buy Now)
   18 Notify box (waitlist reveal)
   ========================================================================== */
(function () {
  'use strict';

  window.FPL = window.FPL || {};
  var FPL = window.FPL;

  /* ========================================================================
     01 — UTILITIES
     ======================================================================== */
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }
  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  function on(el, ev, fn, opts) {
    if (el) el.addEventListener(ev, fn, opts);
  }
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  }
  function debounce(fn, wait) {
    var t;
    return function () {
      var a = arguments,
        c = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(c, a);
      }, wait || 120);
    };
  }
  function safeJSON(str, fallback) {
    try {
      return JSON.parse(str);
    } catch (e) {
      return fallback;
    }
  }
  function store(key, val) {
    try {
      if (val === undefined) {
        var raw = localStorage.getItem(key);
        return raw === null ? null : safeJSON(raw, null);
      }
      localStorage.setItem(key, JSON.stringify(val));
      return val;
    } catch (e) {
      // Private mode / blocked storage — degrade to in-memory.
      FPL._mem = FPL._mem || {};
      if (val === undefined) return FPL._mem[key] === undefined ? null : FPL._mem[key];
      FPL._mem[key] = val;
      return val;
    }
  }
  function money(cents) {
    if (typeof cents !== 'number') return '';
    var fmt = (FPL.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    var amount = (cents / 100).toFixed(2);
    if (fmt.indexOf('amount_no_decimals') > -1) amount = Math.round(cents / 100).toString();
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }
  FPL.$ = $;
  FPL.$$ = $$;
  FPL.money = money;
  FPL.reducedMotion = REDUCED;

  /* Focus trap for drawers and modals */
  var FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  function trapFocus(container, returnTo) {
    if (!container) return function () {};
    var nodes = $$(FOCUSABLE, container).filter(function (n) {
      return n.offsetParent !== null;
    });
    if (!nodes.length) return function () {};
    var first = nodes[0],
      last = nodes[nodes.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', handler);
    first.focus();
    return function release() {
      container.removeEventListener('keydown', handler);
      if (returnTo && returnTo.focus) returnTo.focus();
    };
  }
  FPL.trapFocus = trapFocus;

  /* Scroll lock that preserves position (iOS-safe) */
  var scrollY = 0,
    lockCount = 0;
  function lockScroll() {
    if (lockCount++ > 0) return;
    scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = -scrollY + 'px';
    document.body.style.width = '100%';
  }
  function unlockScroll() {
    if (--lockCount > 0) return;
    lockCount = 0;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }
  FPL.lockScroll = lockScroll;
  FPL.unlockScroll = unlockScroll;

  /* Shared scrim */
  function getScrim() {
    var s = $('#FplScrim');
    if (!s) {
      s = document.createElement('button');
      s.id = 'FplScrim';
      s.className = 'fpl-scrim';
      s.type = 'button';
      s.setAttribute('aria-label', 'Close');
      s.tabIndex = -1;
      document.body.appendChild(s);
    }
    return s;
  }

  /* ========================================================================
     02 — LOGO INTRO
     Fires once per browser session. Never blocks navigation.
     ======================================================================== */
  function initIntro() {
    var intro = $('#FplIntro');
    if (!intro) return;

    var KEY = 'fpl:intro:v1';
    var mode = intro.dataset.mode || 'session';
    var duration = parseInt(intro.dataset.duration, 10) || 1200;
    var seen = false;

    try {
      if (mode === 'session') seen = sessionStorage.getItem(KEY) === '1';
      else if (mode === 'daily') {
        var last = parseInt(localStorage.getItem(KEY) || '0', 10);
        seen = Date.now() - last < 864e5;
      } else if (mode === 'always') seen = false;
    } catch (e) {
      seen = false;
    }

    if (seen || REDUCED) {
      intro.remove();
      document.documentElement.classList.remove('fpl-intro-active');
      return;
    }

    try {
      if (mode === 'session') sessionStorage.setItem(KEY, '1');
      else if (mode === 'daily') localStorage.setItem(KEY, String(Date.now()));
    } catch (e) {}

    var started = performance.now();
    function dismiss() {
      var elapsed = performance.now() - started;
      var wait = Math.max(0, duration - elapsed);
      setTimeout(function () {
        intro.classList.add('is-done');
        document.documentElement.classList.remove('fpl-intro-active');
        setTimeout(function () {
          if (intro.parentNode) intro.remove();
        }, 560);
      }, wait);
    }

    if (document.readyState === 'complete') dismiss();
    else window.addEventListener('load', dismiss, { once: true });

    // Hard ceiling — never hold the page hostage to a stalled asset.
    setTimeout(dismiss, duration + 1400);
    // Any interaction skips it immediately.
    ['pointerdown', 'keydown', 'wheel', 'touchstart'].forEach(function (ev) {
      window.addEventListener(
        ev,
        function () {
          started = 0;
          dismiss();
        },
        { once: true, passive: true }
      );
    });
  }

  /* ========================================================================
     03 — SCROLL REVEAL
     One-time, staggered, IntersectionObserver-based.
     ======================================================================== */
  function initReveal(scope) {
    var nodes = $$('[data-fpl-reveal]:not(.is-revealed)', scope || document);
    if (!nodes.length) return;
    if (REDUCED || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) {
        n.classList.add('is-revealed');
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var group = el.closest('[data-fpl-reveal-group]');
          var delay = 0;
          if (group) {
            var sibs = $$('[data-fpl-reveal]', group);
            delay = Math.min(sibs.indexOf(el), 7) * 70;
          }
          el.style.setProperty('--fpl-reveal-delay', delay + 'ms');
          el.classList.add('is-revealed');
          io.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    nodes.forEach(function (n) {
      io.observe(n);
    });
  }
  FPL.initReveal = initReveal;

  /* ========================================================================
     04 — HEADER
     ======================================================================== */
  function initHeader() {
    var header = $('#FplHeader');
    if (!header) return;
    var overlay = header.classList.contains('fpl-header--overlay');
    var lastY = window.scrollY;
    var threshold = parseInt(header.dataset.stickThreshold, 10) || 80;
    var autoHide = header.dataset.autohide === 'true';
    var ticking = false;

    function update() {
      var y = window.scrollY;
      if (overlay) header.classList.toggle('is-stuck', y > threshold);
      if (autoHide) {
        var goingDown = y > lastY && y > threshold * 3;
        var drawerOpen = document.body.classList.contains('fpl-drawer-open');
        header.classList.toggle('is-hidden', goingDown && !drawerOpen);
      }
      lastY = y;
      ticking = false;
    }
    on(
      window,
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();

    // Keep the CSS var in sync with the real rendered height.
    var sync = debounce(function () {
      document.documentElement.style.setProperty('--fpl-header-h', header.offsetHeight + 'px');
    }, 150);
    on(window, 'resize', sync);
    sync();
  }

  /* ========================================================================
     05 — ANNOUNCEMENT ROTATOR
     ======================================================================== */
  function initAnnounce() {
    $$('[data-fpl-announce]').forEach(function (bar) {
      var slides = $$('.fpl-announce__slide', bar);
      if (slides.length < 2) return;
      var interval = parseInt(bar.dataset.interval, 10) || 5000;
      var i = 0,
        timer = null;
      function go() {
        slides[i].classList.remove('is-active');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
      }
      function start() {
        stop();
        if (!REDUCED) timer = setInterval(go, interval);
      }
      function stop() {
        if (timer) clearInterval(timer);
        timer = null;
      }
      on(bar, 'mouseenter', stop);
      on(bar, 'mouseleave', start);
      on(document, 'visibilitychange', function () {
        document.hidden ? stop() : start();
      });
      start();
    });
  }

  /* ========================================================================
     06 — MOBILE NAV
     ======================================================================== */
  function initMobileNav() {
    var nav = $('#FplMobileNav');
    var toggle = $('#FplBurger');
    if (!nav || !toggle) return;
    var scrim = getScrim();
    var release = null;

    function open() {
      nav.classList.add('is-open');
      scrim.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('fpl-drawer-open');
      lockScroll();
      release = trapFocus(nav, toggle);
    }
    function close() {
      nav.classList.remove('is-open');
      scrim.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('fpl-drawer-open');
      unlockScroll();
      if (release) {
        release();
        release = null;
      }
    }
    on(toggle, 'click', function () {
      nav.classList.contains('is-open') ? close() : open();
    });
    on($('#FplMobileNavClose'), 'click', close);
    on(scrim, 'click', close);
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) close();
    });

    // Sub-menu accordions
    $$('[data-fpl-subtoggle]', nav).forEach(function (btn) {
      on(btn, 'click', function () {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;
        var open = panel.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ========================================================================
     07 — ON ICE WISHLIST
     Storage model:
       • localStorage is always the working copy (instant, offline-safe).
       • Logged-in customers get a namespaced key so a shared device does not
         leak lists between accounts.
       • If FPL.wishlistSyncUrl is set (App Proxy), the list is pulled on load
         and pushed on change, so it follows the customer across devices.
     ======================================================================== */
  var OnIce = (function () {
    var BASE = 'fpl:onice:v1';
    var items = [];
    var syncing = false;

    function key() {
      return FPL.customerId ? BASE + ':' + FPL.customerId : BASE + ':guest';
    }
    function load() {
      items = store(key()) || [];
      if (!Array.isArray(items)) items = [];
      return items;
    }
    function persist() {
      store(key(), items);
      broadcast();
      push();
    }
    function broadcast() {
      document.dispatchEvent(new CustomEvent('fpl:onice:change', { detail: { items: items } }));
      paintAll();
    }
    function has(id) {
      return items.some(function (it) {
        return String(it.id) === String(id);
      });
    }
    function add(item) {
      if (has(item.id)) return false;
      items.unshift(item);
      if (items.length > 100) items.length = 100;
      persist();
      return true;
    }
    function remove(id) {
      var before = items.length;
      items = items.filter(function (it) {
        return String(it.id) !== String(id);
      });
      if (items.length !== before) persist();
      return items.length !== before;
    }
    function toggle(item) {
      return has(item.id) ? (remove(item.id), false) : (add(item), true);
    }
    function all() {
      return items.slice();
    }
    function count() {
      return items.length;
    }

    /* ---- Optional cross-device sync via Shopify App Proxy ---- */
    function pull() {
      if (!FPL.wishlistSyncUrl || !FPL.customerId) return Promise.resolve();
      return fetch(FPL.wishlistSyncUrl + '?customer_id=' + encodeURIComponent(FPL.customerId), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (data) {
          if (!data || !Array.isArray(data.items)) return;
          // Merge: remote wins on conflict, local additions preserved.
          var seen = {};
          var merged = [];
          data.items.concat(items).forEach(function (it) {
            if (!it || seen[it.id]) return;
            seen[it.id] = 1;
            merged.push(it);
          });
          items = merged;
          store(key(), items);
          broadcast();
        })
        .catch(function () {});
    }
    var pushDebounced = debounce(function () {
      if (!FPL.wishlistSyncUrl || !FPL.customerId || syncing) return;
      syncing = true;
      fetch(FPL.wishlistSyncUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ customer_id: FPL.customerId, items: items }),
      })
        .catch(function () {})
        .then(function () {
          syncing = false;
        });
    }, 700);
    function push() {
      pushDebounced();
    }

    /* ---- Paint every button on the page to match state ---- */
    function paintAll() {
      $$('.fpl-ice[data-fpl-ice]').forEach(function (btn) {
        var frozen = has(btn.dataset.fplIce);
        btn.classList.toggle('is-frozen', frozen);
        btn.setAttribute('aria-pressed', frozen ? 'true' : 'false');
        var label = frozen ? btn.dataset.labelOn : btn.dataset.labelOff;
        if (label) btn.setAttribute('aria-label', label);
        var sr = $('.fpl-sr', btn);
        if (sr && label) sr.textContent = label;
      });
      var n = count();
      $$('[data-fpl-ice-count]').forEach(function (el) {
        el.textContent = n;
        el.classList.toggle('is-visible', n > 0);
      });
      $$('[data-fpl-ice-total]').forEach(function (el) {
        el.textContent = n;
      });
    }

    return {
      load: load,
      has: has,
      add: add,
      remove: remove,
      toggle: toggle,
      all: all,
      count: count,
      paint: paintAll,
      pull: pull,
    };
  })();
  FPL.OnIce = OnIce;

  function initOnIce() {
    OnIce.load();
    OnIce.paint();
    OnIce.pull();

    // Delegated click — works for cards injected later (filters, pagination).
    on(
      document,
      'click',
      function (e) {
        var btn = e.target.closest('.fpl-ice[data-fpl-ice]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();

        var item = {
          id: btn.dataset.fplIce,
          handle: btn.dataset.handle || '',
          title: btn.dataset.title || '',
          url: btn.dataset.url || '',
          image: btn.dataset.image || '',
          price: btn.dataset.price || '',
          variant: btn.dataset.variant || '',
          available: btn.dataset.available !== 'false',
          added: Date.now(),
        };

        var nowFrozen = OnIce.toggle(item);
        btn.classList.remove('is-bursting', 'is-thawing');
        void btn.offsetWidth; // restart animation
        btn.classList.add(nowFrozen ? 'is-bursting' : 'is-thawing');
        setTimeout(function () {
          btn.classList.remove('is-bursting', 'is-thawing');
        }, 700);

        if (nowFrozen && btn.dataset.openDrawer === 'true') openIceDrawer();
        announce(nowFrozen ? btn.dataset.labelOn : btn.dataset.labelOff);
      },
      true
    );

    // Drawer open/close
    $$('[data-fpl-ice-open]').forEach(function (b) {
      on(b, 'click', function (e) {
        e.preventDefault();
        openIceDrawer(b);
      });
    });
    on($('#FplIceClose'), 'click', closeIceDrawer);
    on(document, 'keydown', function (e) {
      var d = $('#FplIceDrawer');
      if (e.key === 'Escape' && d && d.classList.contains('is-open')) closeIceDrawer();
    });
    on(getScrim(), 'click', closeIceDrawer);

    document.addEventListener('fpl:onice:change', renderIceDrawer);
    renderIceDrawer();
  }

  var iceRelease = null;
  function openIceDrawer(trigger) {
    var d = $('#FplIceDrawer');
    if (!d) return;
    renderIceDrawer();
    d.classList.add('is-open');
    getScrim().classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('fpl-drawer-open');
    lockScroll();
    iceRelease = trapFocus(d, trigger);
  }
  function closeIceDrawer() {
    var d = $('#FplIceDrawer');
    if (!d || !d.classList.contains('is-open')) return;
    d.classList.remove('is-open');
    getScrim().classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('fpl-drawer-open');
    unlockScroll();
    if (iceRelease) {
      iceRelease();
      iceRelease = null;
    }
  }
  FPL.openIceDrawer = openIceDrawer;

  function renderIceDrawer() {
    var body = $('#FplIceBody');
    if (!body) return;
    var items = OnIce.all();
    var empty = $('#FplIceEmpty');
    var foot = $('#FplIceFoot');

    if (!items.length) {
      body.innerHTML = '';
      if (empty) empty.hidden = false;
      if (foot) foot.hidden = true;
      return;
    }
    if (empty) empty.hidden = true;
    if (foot) foot.hidden = false;

    body.innerHTML = items
      .map(function (it) {
        var img = it.image
          ? '<img class="fpl-onice__thumb" src="' +
            esc(it.image) +
            '" alt="" loading="lazy" width="76" height="95">'
          : '<span class="fpl-onice__thumb"></span>';
        return (
          '<div class="fpl-onice__item" data-ice-row="' +
          esc(it.id) +
          '">' +
          '<a href="' +
          esc(it.url) +
          '" tabindex="-1" aria-hidden="true">' +
          img +
          '</a>' +
          '<div>' +
          '<a class="fpl-onice__name" href="' +
          esc(it.url) +
          '">' +
          esc(it.title) +
          '</a>' +
          (it.variant ? '<div class="fpl-onice__variant">' + esc(it.variant) + '</div>' : '') +
          (it.price ? '<div class="fpl-onice__price">' + esc(it.price) + '</div>' : '') +
          (it.available === false
            ? '<div class="fpl-onice__variant" style="color:var(--fpl-heat)">Sold out</div>'
            : '') +
          '</div>' +
          '<div class="fpl-onice__actions">' +
          '<button type="button" class="fpl-onice__remove" data-ice-remove="' +
          esc(it.id) +
          '">Remove</button>' +
          '</div>' +
          '</div>'
        );
      })
      .join('');

    $$('[data-ice-remove]', body).forEach(function (btn) {
      on(btn, 'click', function () {
        var id = btn.dataset.iceRemove;
        var row = $('[data-ice-row="' + CSS.escape(id) + '"]', body);
        if (row) {
          row.classList.add('is-removing');
          setTimeout(function () {
            OnIce.remove(id);
          }, 280);
        } else {
          OnIce.remove(id);
        }
      });
    });
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Screen-reader live announcements */
  function announce(msg) {
    if (!msg) return;
    var live = $('#FplLive');
    if (!live) {
      live = document.createElement('div');
      live.id = 'FplLive';
      live.className = 'fpl-sr';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('role', 'status');
      document.body.appendChild(live);
    }
    live.textContent = '';
    setTimeout(function () {
      live.textContent = msg;
    }, 60);
  }
  FPL.announce = announce;

  /* ========================================================================
     08 — REVEAL SLIDER (dry / wet)
     ======================================================================== */
  function initRevealSliders(scope) {
    $$('[data-fpl-reveal-slider]', scope || document).forEach(function (el) {
      if (el.dataset.bound === '1') return;
      el.dataset.bound = '1';
      var dragging = false;

      function setPos(pct) {
        pct = Math.max(0, Math.min(100, pct));
        el.style.setProperty('--fpl-reveal-pos', pct + '%');
        el.setAttribute('aria-valuenow', Math.round(pct));
      }
      function fromEvent(e) {
        var rect = el.getBoundingClientRect();
        var x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
        setPos((x / rect.width) * 100);
      }
      function start(e) {
        dragging = true;
        el.style.cursor = 'grabbing';
        fromEvent(e);
      }
      function move(e) {
        if (!dragging) return;
        if (e.cancelable) e.preventDefault();
        fromEvent(e);
      }
      function end() {
        dragging = false;
        el.style.cursor = '';
      }

      on(el, 'pointerdown', start);
      on(window, 'pointermove', move, { passive: false });
      on(window, 'pointerup', end);
      on(el, 'touchstart', start, { passive: true });
      on(el, 'touchmove', move, { passive: false });
      on(el, 'touchend', end);
      // Tap anywhere jumps the handle there
      on(el, 'click', function (e) {
        if (!dragging) fromEvent(e);
      });
      // Keyboard
      on(el, 'keydown', function (e) {
        var cur = parseFloat(el.getAttribute('aria-valuenow')) || 50;
        var step = e.shiftKey ? 10 : 4;
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setPos(cur - step);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          setPos(cur + step);
        } else if (e.key === 'Home') {
          e.preventDefault();
          setPos(0);
        } else if (e.key === 'End') {
          e.preventDefault();
          setPos(100);
        }
      });

      setPos(parseFloat(el.dataset.start) || 50);

      // A single unprompted sweep on first view, so the affordance is obvious.
      if (!REDUCED && 'IntersectionObserver' in window && el.dataset.autohint !== 'false') {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (en) {
              if (!en.isIntersecting) return;
              io.disconnect();
              var t0 = null,
                from = parseFloat(el.dataset.start) || 50;
              function sweep(ts) {
                if (dragging) return;
                if (!t0) t0 = ts;
                var p = Math.min(1, (ts - t0) / 1500);
                var eased = 1 - Math.pow(1 - p, 3);
                var target = from + (from - 22 - from) * Math.sin(eased * Math.PI);
                setPos(target);
                if (p < 1) requestAnimationFrame(sweep);
              }
              setTimeout(function () {
                requestAnimationFrame(sweep);
              }, 400);
            });
          },
          { threshold: 0.4 }
        );
        io.observe(el);
      }
    });
  }

  /* ========================================================================
     09 — COUNTDOWN + UNIT METER
     ======================================================================== */
  function initCountdowns(scope) {
    $$('[data-fpl-countdown]', scope || document).forEach(function (el) {
      var target = Date.parse(el.dataset.fplCountdown);
      if (isNaN(target)) return;
      var live = $('[data-countdown-live]', el);
      var grid = $('[data-countdown-grid]', el);
      var units = {
        d: $('[data-cd="days"]', el),
        h: $('[data-cd="hours"]', el),
        m: $('[data-cd="minutes"]', el),
        s: $('[data-cd="seconds"]', el),
      };
      var timer;
      function pad(n) {
        return n < 10 ? '0' + n : String(n);
      }
      function tick() {
        var diff = target - Date.now();
        if (diff <= 0) {
          clearInterval(timer);
          if (grid) grid.hidden = true;
          if (live) live.hidden = false;
          el.dispatchEvent(new CustomEvent('fpl:countdown:done', { bubbles: true }));
          return;
        }
        var s = Math.floor(diff / 1000);
        if (units.d) units.d.textContent = pad(Math.floor(s / 86400));
        if (units.h) units.h.textContent = pad(Math.floor((s % 86400) / 3600));
        if (units.m) units.m.textContent = pad(Math.floor((s % 3600) / 60));
        if (units.s) units.s.textContent = pad(s % 60);
      }
      tick();
      timer = setInterval(tick, 1000);
    });
  }

  function initMeters(scope) {
    $$('[data-fpl-meter]', scope || document).forEach(function (el) {
      var fill = $('.fpl-meter__fill', el);
      if (!fill) return;
      var pct = Math.max(0, Math.min(100, parseFloat(el.dataset.fplMeter) || 0));
      function run() {
        fill.style.width = pct + '%';
      }
      if (REDUCED || !('IntersectionObserver' in window)) return run();
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) {
              run();
              io.disconnect();
            }
          });
        },
        { threshold: 0.3 }
      );
      io.observe(el);
    });
  }

  /* ========================================================================
     10 — COUNT-UP STATS
     ======================================================================== */
  function initCounters(scope) {
    var nodes = $$('[data-fpl-count]', scope || document);
    if (!nodes.length) return;
    // The real value is already in the HTML. If we cannot animate — reduced
    // motion, no IntersectionObserver, JS half-loaded — we simply leave it
    // alone. The number is never wrong, only ever un-animated.
    if (REDUCED || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          var el = en.target;
          io.unobserve(el);
          var raw = String(el.dataset.fplCount);
          var num = parseFloat(raw.replace(/[^0-9.\-]/g, ''));
          if (isNaN(num)) return; // non-numeric — leave the rendered text as is
          var prefix = raw.slice(0, raw.search(/[0-9\-]/));
          var suffix = raw.slice(raw.search(/[0-9\-]/) + String(num).length);
          var decimals = (String(num).split('.')[1] || '').length;
          var dur = 1500,
            t0 = null;
          function step(ts) {
            if (!t0) t0 = ts;
            var p = Math.min(1, (ts - t0) / dur);
            var eased = 1 - Math.pow(1 - p, 4);
            el.textContent = prefix + (num * eased).toFixed(decimals) + suffix;
            if (p < 1) requestAnimationFrame(step);
            else el.textContent = raw;
          }
          requestAnimationFrame(step);
        });
      },
      { threshold: 0.4 }
    );
    nodes.forEach(function (n) {
      io.observe(n);
    });
  }

  /* ========================================================================
     11 — ACCORDIONS / TABS / MODALS
     ======================================================================== */
  function initAccordions(scope) {
    $$('[data-fpl-acc]', scope || document).forEach(function (acc) {
      if (acc.dataset.bound === '1') return;
      acc.dataset.bound = '1';
      var single = acc.dataset.single === 'true';
      $$('.fpl-acc__btn', acc).forEach(function (btn) {
        on(btn, 'click', function () {
          var panel = document.getElementById(btn.getAttribute('aria-controls'));
          if (!panel) return;
          var isOpen = btn.getAttribute('aria-expanded') === 'true';
          if (single && !isOpen) {
            $$('.fpl-acc__btn', acc).forEach(function (b) {
              b.setAttribute('aria-expanded', 'false');
              var p = document.getElementById(b.getAttribute('aria-controls'));
              if (p) p.classList.remove('is-open');
            });
          }
          btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
          panel.classList.toggle('is-open', !isOpen);
        });
      });
    });
  }

  function initTabs(scope) {
    $$('[data-fpl-tabs]', scope || document).forEach(function (wrap) {
      if (wrap.dataset.bound === '1') return;
      wrap.dataset.bound = '1';
      var tabs = $$('[role="tab"]', wrap);
      function select(tab) {
        tabs.forEach(function (t) {
          var on_ = t === tab;
          t.setAttribute('aria-selected', on_ ? 'true' : 'false');
          t.tabIndex = on_ ? 0 : -1;
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on_;
        });
      }
      tabs.forEach(function (tab, i) {
        on(tab, 'click', function () {
          select(tab);
        });
        on(tab, 'keydown', function (e) {
          var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          var next = tabs[(i + dir + tabs.length) % tabs.length];
          next.focus();
          select(next);
        });
      });
    });
  }

  function initModals(scope) {
    $$('[data-fpl-modal-open]', scope || document).forEach(function (btn) {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      on(btn, 'click', function (e) {
        e.preventDefault();
        openModal(btn.dataset.fplModalOpen, btn);
      });
    });
    $$('[data-fpl-modal]', scope || document).forEach(function (modal) {
      if (modal.dataset.bound === '1') return;
      modal.dataset.bound = '1';
      $$('[data-fpl-modal-close]', modal).forEach(function (c) {
        on(c, 'click', function () {
          closeModal(modal);
        });
      });
      on(modal, 'click', function (e) {
        if (e.target === modal) closeModal(modal);
      });
    });
    on(document, 'keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = $('.fpl-modal.is-open');
      if (open) closeModal(open);
    });
  }
  var modalRelease = null;
  function openModal(id, trigger) {
    var m = document.getElementById(id);
    if (!m) return;
    m.classList.add('is-open');
    m.setAttribute('aria-hidden', 'false');
    lockScroll();
    modalRelease = trapFocus(m, trigger);
  }
  function closeModal(m) {
    if (!m) return;
    m.classList.remove('is-open');
    m.setAttribute('aria-hidden', 'true');
    unlockScroll();
    if (modalRelease) {
      modalRelease();
      modalRelease = null;
    }
  }
  FPL.openModal = openModal;

  /* ========================================================================
     12 — STICKY ADD TO CART
     ======================================================================== */
  function initStickyATC() {
    var bar = $('#FplStickyATC');
    if (!bar) return;
    var sentinel = $('#FplATCSentinel');
    if (!sentinel || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          bar.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 }
    );
    io.observe(sentinel);
    // Proxy the sticky button to the real form button — or, on an
    // in-development product where there is no real submit button at all,
    // to the notify-me toggle instead, so the sticky bar always does
    // something once it's showing.
    on($('[data-sticky-submit]', bar), 'click', function (e) {
      e.preventDefault();
      if (this.hasAttribute('data-sticky-indev')) {
        var notifyOpen = $('[data-fpl-notify-open]');
        if (notifyOpen) {
          notifyOpen.click();
          var panel = document.getElementById(notifyOpen.getAttribute('aria-controls'));
          if (panel) panel.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'center' });
        }
        return;
      }
      var real = $('product-form button[type="submit"], form[action*="/cart/add"] button[type="submit"]');
      if (real) real.click();
    });
  }

  /* ========================================================================
     13 — QUANTITY + QUICK ORDER
     ======================================================================== */
  function initQty(scope) {
    $$('[data-fpl-qty]', scope || document).forEach(function (wrap) {
      if (wrap.dataset.bound === '1') return;
      wrap.dataset.bound = '1';
      var input = $('input', wrap);
      if (!input) return;
      $$('button', wrap).forEach(function (btn) {
        on(btn, 'click', function () {
          var step = parseInt(input.step, 10) || 1;
          var min = parseInt(input.min, 10) || 0;
          var max = input.max ? parseInt(input.max, 10) : Infinity;
          var v = parseInt(input.value, 10) || 0;
          v += btn.dataset.qty === 'up' ? step : -step;
          input.value = Math.max(min, Math.min(max, v));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  function initQuickOrder(scope) {
    $$('[data-fpl-quickorder]', scope || document).forEach(function (form) {
      if (form.dataset.bound === '1') return;
      form.dataset.bound = '1';
      var submit = $('[data-quickorder-submit]', form);
      var totalEl = $('[data-quickorder-total]', form);
      var msg = $('[data-quickorder-msg]', form);

      function recalc() {
        var total = 0;
        $$('input[data-variant-id]', form).forEach(function (i) {
          total += (parseInt(i.value, 10) || 0) * (parseInt(i.dataset.price, 10) || 0);
        });
        if (totalEl) totalEl.textContent = money(total);
        if (submit) submit.disabled = total === 0;
      }
      on(form, 'change', recalc);
      on(form, 'input', recalc);
      recalc();

      on(form, 'submit', function (e) {
        e.preventDefault();
        var lines = [];
        $$('input[data-variant-id]', form).forEach(function (i) {
          var q = parseInt(i.value, 10) || 0;
          if (q > 0) lines.push({ id: parseInt(i.dataset.variantId, 10), quantity: q });
        });
        if (!lines.length) return;
        if (submit) {
          submit.disabled = true;
          submit.dataset.label = submit.textContent;
          submit.textContent = 'Adding…';
        }
        fetch(window.routes ? window.routes.cart_add_url : '/cart/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lines }),
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (data) {
            if (data.status) throw new Error(data.description || data.message);
            if (msg) {
              msg.textContent = 'Added to cart.';
              msg.className = 'fpl-form-msg fpl-form-msg--ok';
            }
            $$('input[data-variant-id]', form).forEach(function (i) {
              i.value = 0;
            });
            recalc();
            refreshCart();
            announce('Added to cart');
          })
          .catch(function (err) {
            if (msg) {
              msg.textContent = err.message || 'Something went wrong. Please try again.';
              msg.className = 'fpl-form-msg fpl-form-msg--err';
            }
          })
          .then(function () {
            if (submit) {
              submit.disabled = false;
              if (submit.dataset.label) submit.textContent = submit.dataset.label;
            }
            recalc();
          });
      });
    });
  }

  /* ========================================================================
     14 — UNIT TOGGLE (cm / in)
     ======================================================================== */
  function initUnitToggle(scope) {
    $$('[data-fpl-units]', scope || document).forEach(function (wrap) {
      if (wrap.dataset.bound === '1') return;
      wrap.dataset.bound = '1';
      var buttons = $$('button[data-unit]', wrap);
      var target = document.getElementById(wrap.dataset.fplUnits) || wrap.parentElement;

      function apply(unit) {
        buttons.forEach(function (b) {
          b.setAttribute('aria-pressed', b.dataset.unit === unit ? 'true' : 'false');
        });
        if (!target) return;
        $$('[data-cm]', target).forEach(function (cell) {
          var cm = parseFloat(cell.dataset.cm);
          if (isNaN(cm)) return;
          cell.textContent = unit === 'in' ? (cm / 2.54).toFixed(1) : cm.toFixed(1);
        });
        $$('[data-unit-label]', target).forEach(function (l) {
          l.textContent = unit === 'in' ? 'in' : 'cm';
        });
        store('fpl:units', unit);
      }
      buttons.forEach(function (b) {
        on(b, 'click', function () {
          apply(b.dataset.unit);
        });
      });
      apply(store('fpl:units') || wrap.dataset.default || 'cm');
    });
  }

  /* ========================================================================
     15 — MARQUEE
     Duplicates the group so the loop is seamless at any width.
     ======================================================================== */
  function initMarquee(scope) {
    $$('[data-fpl-marquee]', scope || document).forEach(function (el) {
      if (el.dataset.bound === '1') return;
      el.dataset.bound = '1';
      var track = $('.fpl-marquee__track', el);
      var group = $('.fpl-marquee__group', track);
      if (!track || !group) return;
      var clone = group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });
  }

  /* ========================================================================
     16 — CART HELPERS
     ======================================================================== */
  function refreshCart() {
    // Ask Dawn's own cart components to re-render, if present.
    if (window.PUB_SUB_EVENTS && window.publish) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'fpl' });
    }
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      fetch(window.Shopify && window.Shopify.routes ? '/cart?view=drawer' : '/cart?view=drawer')
        .then(function (r) {
          return r.text();
        })
        .then(function () {
          if (drawer.classList) drawer.classList.remove('is-empty');
        })
        .catch(function () {});
    }
    // Update any FPL cart counters.
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        $$('[data-fpl-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.classList.toggle('is-visible', cart.item_count > 0);
        });
        document.dispatchEvent(new CustomEvent('fpl:cart:update', { detail: cart }));
      })
      .catch(function () {});
  }
  FPL.refreshCart = refreshCart;

  function initFooterAccordions() {
    $$('.fpl-footer__col--collapsible').forEach(function (col) {
      var btn = $('.fpl-footer__col-title', col);
      if (!btn || btn.tagName !== 'BUTTON') return;
      on(btn, 'click', function () {
        var open = col.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ========================================================================
     17 — BUY BOX (BUY NOW)
     Second button next to Add to Cart / Reserve Now. Adds the current
     variant to the cart the same way the real form would, then sends the
     customer straight to checkout instead of the cart page — one click,
     no detour. Falls back to a visible inline error if the add fails,
     using the same error slot Dawn's add-to-cart form already renders.
     ======================================================================== */
  function initBuyNow(scope) {
    $$('[data-fpl-buy-now]', scope || document).forEach(function (btn) {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';

      on(btn, 'click', function () {
        if (btn.disabled || btn.hasAttribute('aria-disabled')) return;

        var root = btn.closest('product-form') || btn.closest('.product-form') || document;
        var idInput = $('.product-variant-id', root) || $('input[name="id"]', root);
        if (!idInput || !idInput.value) return;

        var qtyInput = $('input[name="quantity"]', root);
        var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        var labelEl = $('[data-fpl-buy-now-label]', btn);
        var originalLabel = labelEl ? labelEl.textContent : null;
        btn.disabled = true;
        if (labelEl) labelEl.textContent = 'Adding…';

        var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
        var checkoutUrl = addUrl.replace(/\/cart\/add(\.js)?(\?.*)?$/, '/checkout');

        function fail(message) {
          btn.disabled = false;
          if (labelEl) labelEl.textContent = originalLabel;
          var errWrap = $('.product-form__error-message-wrapper', root);
          var errMsg = $('.product-form__error-message', root);
          if (errWrap && errMsg) {
            errMsg.textContent = message;
            errWrap.hidden = false;
          } else {
            announce(message);
          }
        }

        fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: idInput.value, quantity: quantity }),
        })
          .then(function (r) {
            return r.json().then(function (data) {
              return { ok: r.ok, data: data };
            });
          })
          .then(function (res) {
            if (!res.ok || res.data.status) {
              throw new Error((res.data && (res.data.description || res.data.message)) || 'Something went wrong. Please try again.');
            }
            window.location.href = checkoutUrl;
          })
          .catch(function (err) {
            fail(err.message || 'Something went wrong. Please try again.');
          });
      });
    });
  }
  FPL.initBuyNow = initBuyNow;

  /* ========================================================================
     18 — NOTIFY BOX (waitlist reveal)
     Both CTAs on an in-development product open the same inline signup
     panel — a one-way reveal, not a toggle, since the goal is to get the
     form in front of the customer, not to let them dismiss it.
     ======================================================================== */
  function initNotifyBox(scope) {
    $$('[data-fpl-notify]', scope || document).forEach(function (wrap) {
      if (wrap.dataset.bound === '1') return;
      wrap.dataset.bound = '1';

      var panel = $('.fpl-notify__panel', wrap);
      if (!panel) return;
      var openers = $$('[data-fpl-notify-open]', wrap);

      openers.forEach(function (btn) {
        on(btn, 'click', function () {
          var wasHidden = panel.hidden;
          panel.hidden = false;
          openers.forEach(function (b) {
            b.setAttribute('aria-expanded', 'true');
          });
          if (wasHidden) {
            var input = $('input[type="email"]', panel);
            if (input) input.focus();
          }
        });
      });
    });
  }
  FPL.initNotifyBox = initNotifyBox;

  /* ========================================================================
     BOOT
     ======================================================================== */
  function boot(scope) {
    initReveal(scope);
    initRevealSliders(scope);
    initCountdowns(scope);
    initMeters(scope);
    initCounters(scope);
    initAccordions(scope);
    initTabs(scope);
    initModals(scope);
    initQty(scope);
    initQuickOrder(scope);
    initUnitToggle(scope);
    initMarquee(scope);
    initBuyNow(scope);
    initNotifyBox(scope);
    OnIce.paint();
  }
  FPL.boot = boot;

  // The intro must start before paint settles — run it as early as possible.
  initIntro();

  ready(function () {
    initHeader();
    initAnnounce();
    initMobileNav();
    initOnIce();
    initStickyATC();
    initFooterAccordions();
    boot(document);
  });

  // Theme editor: re-bind whenever a section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
    initHeader();
    initAnnounce();
    initMobileNav();
    initStickyATC();
  });
  document.addEventListener('shopify:section:select', function (e) {
    boot(e.target);
  });
})();
